import React, { useEffect, useRef, useState } from "react";

type CounterProps = {
  end: number;
  start?: number;
  duration?: number; // in milliseconds
  decimals?: number;
  prefix?: string;
  suffix?: string;
  separator?: string; // like ","
  className?: string;
};

const easeOutQuad = (t: number) => t * (2 - t);

const Counter: React.FC<CounterProps> = ({
  end,
  start = 0,
  duration = 2000,
  decimals = 0,
  prefix = "",
  suffix = "",
  separator = ",",
  className = "",
}) => {
  const [currentValue, setCurrentValue] = useState(start);
const frame = useRef<number>(0);
  const startTime = useRef<number>(0);

  useEffect(() => {
    const animate = (timestamp: number) => {
      if (!startTime.current) startTime.current = timestamp;
      const progress = Math.min((timestamp - startTime.current) / duration, 1);
      const easedProgress = easeOutQuad(progress);
      const value = start + (end - start) * easedProgress;

      setCurrentValue(value);

      if (progress < 1) {
        frame.current = requestAnimationFrame(animate);
      } else {
        cancelAnimationFrame(frame.current!);
      }
    };

    frame.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame.current!);
  }, [end, start, duration]);

  const formatNumber = (value: number) => {
    const fixed = value.toFixed(decimals);
    const parts = fixed.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, separator);
    return parts.join(".");
  };

  return (
    <span
      className={className}
      aria-label={`${prefix}${formatNumber(currentValue)}${suffix}`}
    >
      {prefix}
      {formatNumber(currentValue)}
      {suffix}
    </span>
  );
};

export default Counter;
